import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, Shield, Activity, Building2, Wallet, 
  FileText, CloudRain, ArrowUp, ArrowDown, ChevronRight, 
  Search, Plus, Eye, Edit2, Trash2, Bell, Settings as SettingsIcon, FileBox
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import styles from './SuperadminDashboard.module.css';

// Charts use data from the backend via 'stats'

const StatCard = ({ title, value, trend, trendUp, icon: Icon, colorClass, onClick }) => (
  <div className={`${styles.statCard} ${colorClass}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
    <div className={styles.statIconWrapper}>
      <Icon size={24} />
    </div>
    <div className={styles.statContent}>
      <h3>{title}</h3>
      <p>{value}</p>
      <div className={styles.trendIndicator} style={{ color: trendUp ? '#16a34a' : '#ea580c' }}>
        {trendUp ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
        <span>{trend}</span>
      </div>
    </div>
  </div>
);

const SuperadminDashboard = () => {
  const [stats, setStats] = useState({
    totalAdmins: 0,
    totalResidents: 0,
    totalSecurity: 0,
    totalSocieties: 0,
    totalComplaints: 0,
    pendingComplaints: 0,
    complaintsData: [],
    totalCollection: 0,
    collectionData: [],
    activeGates: 0
  });
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        setUserName(userInfo.fullName || 'Admin');

        const [statsRes, adminsRes] = await Promise.all([
          api.get('/superadmin/stats'),
          api.get('/superadmin/admins')
        ]);
        
        setStats(statsRes.data);
        setAdmins(adminsRes.data.slice(0, 5)); // Just grab top 5 for preview
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  if (loading) {
    return <div className={styles.loading}>Loading Dashboard...</div>;
  }

  const totalComplaints = stats.totalComplaints || 0;

  return (
    <div className={styles.dashboardContainer}>
      
      {/* 1. Welcome Banner */}
      <div className={styles.welcomeBanner}>
        <div className={styles.bannerGraphics}></div>
        <div className={styles.welcomeContent}>
          <h1>Good Afternoon, {userName.split(' ')[0]}! 👋</h1>
          <p>You have full access to manage all societies, admins and system settings.</p>
          <div className={styles.weatherWidget}>
            <CloudRain size={18} color="#64748b" />
            29°C • Rain • Indore, India
          </div>
        </div>
        <div className={styles.quoteSection}>
          <span>"Safer Communities<br/>Stronger Together"</span>
        </div>
      </div>

      {/* 2. Stats Grid */}
      <div className={styles.statsGrid}>
        <StatCard title="Total Businesses" value={stats.totalSocieties || 0} trend="+1 this month" trendUp={true} icon={Building2} colorClass={styles.societiesCard} onClick={() => navigate('/superadmin/businesses')} />
        <StatCard title="Total Admins" value={stats.totalAdmins} trend="+1 this month" trendUp={true} icon={Users} colorClass={styles.adminsCard} onClick={() => navigate('/superadmin/system-users')} />
        <StatCard title="Total Residents" value={stats.totalResidents} trend="+12 this month" trendUp={true} icon={UserPlus} colorClass={styles.residentsCard} onClick={() => navigate('/superadmin/system-users')} />
        <StatCard title="Total Complaints" value={stats.totalComplaints || 0} trend={`${stats.pendingComplaints || 0} pending`} trendUp={false} icon={FileText} colorClass={styles.complaintsCard} onClick={() => navigate('/superadmin/complaints')} />
        <StatCard title="Total Collection" value={`₹${(stats.totalCollection || 0).toLocaleString()}`} trend="+18% this month" trendUp={true} icon={Wallet} colorClass={styles.collectionCard} onClick={() => navigate('/superadmin/payments')} />
        <StatCard title="Active Gates" value={stats.activeGates || 0} trend="All Operational" trendUp={true} icon={Shield} colorClass={styles.gatesCard} onClick={() => navigate('/superadmin/security-logs')} />
      </div>

      {/* 3. Middle Section (Charts & Actions) */}
      <div className={styles.middleSection}>
        
        {/* Complaints Overview */}
        <div className={styles.chartCard}>
          <div className={styles.cardHeader}>
            <h3>Complaints Overview</h3>
            <button className={styles.viewAllBtn} onClick={() => navigate('/superadmin/complaints')}>View All <ChevronRight size={14}/></button>
          </div>
          <div className={styles.donutContainer}>
            <div style={{ width: 140, height: 140, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.complaintsData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={65}
                    dataKey="value"
                    stroke="none"
                  >
                    {(stats.complaintsData || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <span style={{ fontSize: '20px', fontWeight: 'bold' }}>{totalComplaints}</span><br/>
                <span style={{ fontSize: '10px', color: '#64748b' }}>Total</span>
              </div>
            </div>
            <div className={styles.donutLegend}>
              {(stats.complaintsData || []).map(item => (
                <div key={item.name} className={styles.legendItem}>
                  <div className={styles.legendColor} style={{ backgroundColor: item.color }}></div>
                  <span>{item.value} {item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Collection Overview */}
        <div className={styles.chartCard}>
          <div className={styles.cardHeader}>
            <h3>Collection Overview</h3>
            <select className={styles.timeSelect} defaultValue="30">
              <option value="30">Last 30 Days</option>
              <option value="90">Last 3 Months</option>
            </select>
          </div>
          <div>
            <h4 className={styles.collectionTotal}>₹{(stats.totalCollection || 0).toLocaleString()}</h4>
            <div className={styles.collectionTrend}>
              <ArrowUp size={14}/> 18% from last month
            </div>
            <div style={{ width: '100%', height: 220, marginTop: '20px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.collectionData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 12, fill: '#64748b' }} 
                    tickFormatter={(val) => val >= 1000 ? `₹${(val/1000).toFixed(1)}K` : `₹${val}`} 
                  />
                  <Tooltip 
                    cursor={{ fill: '#f8fafc' }} 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Collection']}
                    labelStyle={{ color: '#0f172a', fontWeight: 'bold', marginBottom: '4px' }}
                  />
                  <Bar dataKey="amount" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className={styles.chartCard}>
          <div className={styles.cardHeader}>
            <h3>Quick Actions</h3>
          </div>
          <div className={styles.actionsGrid}>
            <button className={`${styles.actionBtn} ${styles.actionAddAdmin}`} onClick={() => navigate('/superadmin/system-users')}>
              <UserPlus size={24} />
              <span>Add Admin</span>
            </button>
            <button className={`${styles.actionBtn} ${styles.actionAddSociety}`} onClick={() => navigate('/superadmin/businesses')}>
              <Building2 size={24} />
              <span>Add Business</span>
            </button>
            <button className={`${styles.actionBtn} ${styles.actionNotification}`} onClick={() => toast.success('Notifications coming soon')}>
              <Bell size={24} />
              <span>Send Notification</span>
            </button>
            <button className={`${styles.actionBtn} ${styles.actionReports}`} onClick={() => navigate('/superadmin/reports')}>
              <FileBox size={24} />
              <span>View Reports</span>
            </button>
            <button className={`${styles.actionBtn} ${styles.actionSettings}`} onClick={() => navigate('/superadmin/settings')}>
              <SettingsIcon size={24} />
              <span>System Settings</span>
            </button>
            <button className={`${styles.actionBtn} ${styles.actionLogs}`} onClick={() => navigate('/superadmin/audit-logs')}>
              <Activity size={24} />
              <span>View Logs</span>
            </button>
          </div>
        </div>

      </div>

      {/* 4. Table Section */}
      <div className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <div>
            <h3>Recent Admins</h3>
            <p>Overview of recently added system administrators</p>
          </div>
          <div className={styles.tableControls}>
            <div className={styles.searchBar}>
              <Search size={16} className={styles.searchIcon} />
              <input type="text" placeholder="Search admins by name or email..." />
            </div>
          </div>
        </div>
        
        <div className={styles.tableResponsive}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined On</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin, index) => (
                <tr key={admin._id}>
                  <td>{index + 1}</td>
                  <td>
                    <div className={styles.adminNameCell}>
                      <div className={styles.avatar}>{admin.fullName.charAt(0).toUpperCase()}</div>
                      {admin.fullName}
                    </div>
                  </td>
                  <td>{admin.email}</td>
                  <td>{admin.phone || 'N/A'}</td>
                  <td>
                    <span className={`${styles.roleBadge} ${admin.role === 'admin' ? styles.roleAdmin : styles.roleSecurity}`}>
                      {admin.role.charAt(0).toUpperCase() + admin.role.slice(1)}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${admin.approvalStatus === 'Approved' ? styles.statusApproved : styles.statusPending}`}>
                      {admin.approvalStatus || 'Approved'}
                    </span>
                  </td>
                  <td>{new Date(admin.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                </tr>
              ))}
              {admins.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>No admins found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className={styles.pagination}>
          <span>Showing 1 to {admins.length} of {admins.length} admins</span>
          <div className={styles.pageControls}>
            <button className={styles.pageBtn}>Previous</button>
            <button className={`${styles.pageBtn} ${styles.active}`}>1</button>
            <button className={styles.pageBtn}>Next</button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SuperadminDashboard;
